import React, { useState, useEffect, useRef, useCallback } from 'react';
import './mapStyles.css'; // Add this for custom styles
import {
    GoogleMap,
    LoadScript,
    Marker,
    InfoWindow,
} from '@react-google-maps/api';
import { Button } from 'tabler-react';
import { googleMapAPIKey } from '../../lib/constants';
import Geocode from 'react-geocode';
import SearchPlaces from './searchPlaces';

// Move libraries array outside component to prevent re-renders
const LIBRARIES = ['places'];

// Default position (will be overridden by geolocation if available)
const defaultPosition = {
    lat: 40.7128, // New York City coordinates as fallback
    lng: -74.006,
};

const containerStyle = {
    width: '100%',
    height: '250px',
    borderRadius: '8px',
    overflow: 'hidden',
};

const Map = ({ saveMeetingPoint }) => {
    const [location, setLocation] = useState(defaultPosition);
    const [map, setMap] = useState(null);
    const [showInfo, setShowInfo] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [mapLoadError, setMapLoadError] = useState(null);
    const [isGettingLocation, setIsGettingLocation] = useState(true);
    const locationRef = useRef(defaultPosition);

    // Keep ref in sync with location state
    useEffect(() => {
        locationRef.current = location;
    }, [location]);

    // Set up Geocode and get current position
    useEffect(() => {
        Geocode.setApiKey(googleMapAPIKey);

        // Get current position if geolocation is available
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const currentPosition = {
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    };
                    setLocation(currentPosition);
                    setIsGettingLocation(false);
                },
                (error) => {
                    console.warn(
                        'Error getting current position:',
                        error.message
                    );
                    setIsGettingLocation(false);
                },
                { timeout: 10000, enableHighAccuracy: true }
            );
        } else {
            setIsGettingLocation(false);
        }
    }, []);

    const onLoad = useCallback(function callback(map) {
        // Set the map instance and mark as loaded
        setMap(map);
        setIsLoaded(true);
        setMapLoadError(null);

        // Center the map on the current location
        if (
            locationRef.current &&
            locationRef.current.lat !== defaultPosition.lat &&
            locationRef.current.lng !== defaultPosition.lng
        ) {
            map.setCenter(locationRef.current);
        }
    }, []);

    const onUnmount = useCallback(function callback() {
        setMap(null);
        setIsLoaded(false);
    }, []);

    const onCenterChanged = () => {
        if (map) {
            const newLocation = map.getCenter().toJSON();
            // Only update if location actually changed
            if (
                Math.abs(newLocation.lat - locationRef.current.lat) > 0.0001 ||
                Math.abs(newLocation.lng - locationRef.current.lng) > 0.0001
            ) {
                setLocation(newLocation);
            }
        }
    };

    const pickPoint = () => {
        if (!location.lat || !location.lng) return;

        Geocode.fromLatLng(location.lat, location.lng).then(
            (response) => {
                if (response.results && response.results.length > 0) {
                    const address =
                        response.results[0].formatted_address ||
                        'Unknown location';
                    saveMeetingPoint({
                        lat: location.lat.toString(),
                        lng: location.lng.toString(),
                        name: address,
                    });
                    setShowInfo(true);
                } else {
                    saveMeetingPoint({
                        lat: location.lat.toString(),
                        lng: location.lng.toString(),
                        name: 'Unknown location',
                    });
                    setShowInfo(true);
                }
            },
            (error) => {
                console.error('Geocoding error:', error);
                saveMeetingPoint({
                    lat: location.lat.toString(),
                    lng: location.lng.toString(),
                    name: 'Location coordinates only',
                });
                setShowInfo(true);
            }
        );
    };

    return (
        <>
            {mapLoadError && (
                <div className="error-message">{mapLoadError}</div>
            )}

            {!isLoaded && !mapLoadError && (
                <div className="loading-container">
                    <div style={{ textAlign: 'center' }}>
                        <div className="spinner"></div>
                        <p>
                            {isGettingLocation
                                ? 'Getting your location...'
                                : 'Loading map...'}
                        </p>
                    </div>
                </div>
            )}

            <LoadScript
                googleMapsApiKey={googleMapAPIKey}
                libraries={LIBRARIES} // Use static constant
                onLoad={() => {
                    console.log('Google Maps API loaded successfully');
                    setMapLoadError(null);
                }}
                onError={(error) => {
                    console.error('Google Maps loading error:', error);
                    setMapLoadError(
                        'Failed to load Google Maps. Please check your API key and internet connection.'
                    );
                }}
            >
                <GoogleMap
                    mapContainerStyle={containerStyle}
                    center={location}
                    zoom={14}
                    onLoad={onLoad}
                    onUnmount={onUnmount}
                    onCenterChanged={onCenterChanged}
                    options={{
                        fullscreenControl: false,
                        streetViewControl: false,
                        mapTypeControl: false,
                        zoomControl: true,
                        scrollwheel: true,
                        disableDoubleClickZoom: false,
                    }}
                >
                    <Marker position={location} />

                    {showInfo && (
                        <InfoWindow
                            position={location}
                            onCloseClick={() => setShowInfo(false)}
                        >
                            <div className="info-window">
                                <div className="info-window-title">
                                    Meeting Point Selected
                                </div>
                                <div className="info-window-coords">
                                    Lat: {location.lat.toFixed(6)}, Lng:{' '}
                                    {location.lng.toFixed(6)}
                                </div>
                            </div>
                        </InfoWindow>
                    )}

                    {isLoaded && (
                        <div
                            style={{
                                position: 'absolute',
                                top: '10px',
                                left: '10px',
                                right: '10px',
                                zIndex: 1000,
                                backgroundColor: 'white',
                                padding: '10px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                            }}
                        >
                            <SearchPlaces
                                changeLocation={(newLoc) => {
                                    setLocation(newLoc);
                                    if (map) {
                                        map.panTo(newLoc);
                                    }
                                }}
                            />
                        </div>
                    )}
                </GoogleMap>
            </LoadScript>

            <Button
                color="primary"
                size="sm"
                className="save-button d-block ml-auto mt-8"
                onClick={pickPoint}
                disabled={!isLoaded}
            >
                Save meeting point
            </Button>
        </>
    );
};

export default Map;
